import React from "react";
import { rosterScheduleKey } from "../../employeeMatch";
import { ganttDisplayRoleKey, schedulingGroupFor } from "../../lib/roles";
import { softoneValueWhenInternalBlank, slotKey } from "../../lib/softoneSlotSync";
import { hoursBetweenHhmm, NEXT_DAY_END_HOURS, shiftTimeToMins } from "../../shiftTimeUtils";

export function resolveEmployeeName(code, staffList) {
  const raw = String(code ?? "").trim();
  if (!raw) return "";
  if (!/^\d+$/.test(raw)) return raw;
  const match = (staffList || []).find(
    (e) => String(e?.employee_code ?? "").trim() === raw
  );
  const name = match?.name != null ? String(match.name).trim() : "";
  return name || raw;
}

export function slotDraftDisplayName(slot, staffList = []) {
  const name = String(slot?.employee_name || "").trim();
  const code = String(slot?.employee_code || "").trim();
  if (name) return resolveEmployeeName(name, staffList);
  return resolveEmployeeName(code, staffList);
}

export function isDraftSlotPlaceholder(slot) {
  const label = String(slot?.employee_name || "").trim();
  return !label || label === "__gap__" || label.startsWith("Optimal_") || label.startsWith("OPTIMAL_");
}

export const SCHEDULE_DIRTY_CELL_SEP = "\u0000";

export function normScheduleCellVal(v) {
  return v == null || v === "" ? null : String(v);
}

export function isScheduleCellDirtyAgainstBaseline(localVal, baselineVal) {
  return normScheduleCellVal(baselineVal) !== normScheduleCellVal(localVal);
}

/** True when any cell in the week grid differs from the last persisted baseline. */
export function isScheduleWeekDirtyAgainstBaseline(currentSched, baselineSched) {
  const current = currentSched || {};
  const baseline = baselineSched || {};
  const empIds = new Set([...Object.keys(current), ...Object.keys(baseline)]);
  for (const empId of empIds) {
    const curEmp = current[empId] || {};
    const baseEmp = baseline[empId] || {};
    const dks = new Set([...Object.keys(curEmp), ...Object.keys(baseEmp)]);
    for (const dk of dks) {
      if (isScheduleCellDirtyAgainstBaseline(curEmp[dk], baseEmp[dk])) return true;
    }
  }
  return false;
}

export function dirtyCellKey(empId, dk) {
  return `${empId}${SCHEDULE_DIRTY_CELL_SEP}${dk}`;
}

export function parseDirtyCellKey(key) {
  const sep = key.indexOf(SCHEDULE_DIRTY_CELL_SEP);
  if (sep < 0) return [key, ""];
  return [key.slice(0, sep), key.slice(sep + 1)];
}

/**
 * Merge server-fetched schedule with unsaved local grid edits.
 * Cells whose local value differs from the pre-fetch persisted baseline keep the local value.
 */
export function mergeFetchedSchedulePreservingDirtyLocal(fetchedSched, localSched, oldBaseline) {
  const merged = JSON.parse(JSON.stringify(fetchedSched || {}));
  const dirtyKeys = new Set();
  const conflicts = [];
  const local = localSched || {};
  const baseline = oldBaseline || {};
  const fetched = fetchedSched || {};

  Object.keys(local).forEach((empId) => {
    const empLocal = local[empId] || {};
    Object.keys(empLocal).forEach((dk) => {
      const localVal = empLocal[dk];
      const baselineVal = baseline[empId]?.[dk];
      if (!isScheduleCellDirtyAgainstBaseline(localVal, baselineVal)) return;

      dirtyKeys.add(dirtyCellKey(empId, dk));
      if (!merged[empId]) merged[empId] = {};
      merged[empId][dk] = localVal;

      const fetchedVal = fetched[empId]?.[dk];
      const normFetched = normScheduleCellVal(fetchedVal);
      const normBaseline = normScheduleCellVal(baselineVal);
      const normLocal = normScheduleCellVal(localVal);
      if (normFetched !== normBaseline && normFetched !== normLocal) {
        conflicts.push({
          empId,
          dk,
          localVal,
          fetchedVal: fetchedVal ?? "",
          baselineVal: baselineVal ?? "",
        });
      }
    });
  });

  return { merged, dirtyKeys, conflicts };
}

export function mergeScheduleConflictCellMaps(existingMap, newConflicts) {
  const next = JSON.parse(JSON.stringify(existingMap || {}));
  (newConflicts || []).forEach((c) => {
    if (!next[c.empId]) next[c.empId] = {};
    next[c.empId][c.dk] = {
      fetchedVal: c.fetchedVal,
      baselineVal: c.baselineVal,
      localVal: c.localVal,
    };
  });
  return next;
}

export const SCHEDULE_TOAST_DEFAULT_MS = 3000;
export const SCHEDULE_CONFLICT_TOAST_MS = 8000;

export function scheduleToastMessage(toast) {
  if (toast == null) return "";
  if (typeof toast === "string") return toast;
  return String(toast.message ?? "");
}

export function scheduleToastDurationMs(toast) {
  if (toast != null && typeof toast === "object" && Number.isFinite(Number(toast.durationMs))) {
    return Number(toast.durationMs);
  }
  return SCHEDULE_TOAST_DEFAULT_MS;
}

export function scheduleToastTone(toast) {
  if (toast != null && typeof toast === "object" && toast.tone) {
    return String(toast.tone);
  }
  return "default";
}

export function formatScheduleMergeConflictToast(conflicts, nameByEmpId, lang) {
  if (!conflicts?.length) return null;
  const t = (en, gr) => (lang === "gr" ? gr : en);
  const resolveName = (empId) => nameByEmpId.get(empId) || empId;

  if (conflicts.length === 1) {
    const c = conflicts[0];
    const name = resolveName(c.empId);
    return t(
      `${name}'s ${c.dk} shift was updated elsewhere — your edit is still showing, review before saving.`,
      `Η βάρδια ${c.dk} του/της ${name} ενημερώθηκε αλλού — η δική σας επεξεργασία εμφανίζεται ακόμα, ελέγξτε πριν την αποθήκευση.`,
    );
  }

  return t(
    `${conflicts.length} shifts were updated elsewhere while you were editing — review before saving.`,
    `${conflicts.length} βάρδιες ενημερώθηκαν αλλού ενώ επεξεργαζόσασταν — ελέγξτε πριν την αποθήκευση.`,
  );
}

/** For dirty cells, pick old or fetched auxiliary per-cell metadata (status, loan markers). */
export function mergeAuxMapForDirtyCells(fetchedMap, oldMap, dirtyKeys, preferFetched) {
  const merged = JSON.parse(JSON.stringify(fetchedMap || {}));
  dirtyKeys.forEach((key) => {
    const [empId, dk] = parseDirtyCellKey(key);
    const fetchedVal = fetchedMap?.[empId]?.[dk];
    const oldVal = oldMap?.[empId]?.[dk];
    const pick = preferFetched && fetchedVal !== undefined ? fetchedVal : oldVal;
    if (pick !== undefined) {
      if (!merged[empId]) merged[empId] = {};
      merged[empId][dk] = pick;
    } else if (merged[empId]) {
      delete merged[empId][dk];
      if (Object.keys(merged[empId]).length === 0) delete merged[empId];
    }
  });
  return merged;
}

function pushTimeDraftSlot(slots, emp, empId, val, source) {
  if (!val || val === "OFF" || SPECIAL_STATUSES.find((s) => s.value === val)) return;
  const parts = String(val).split("-");
  if (parts.length !== 2) return;
  const start = parts[0].trim().slice(0, 5);
  const end = parts[1].trim().slice(0, 5);
  if (!start || !end) return;
  const hours = hoursBetweenHhmm(start, end);
  slots.push({
    employee_name: emp.name || empId,
    employee_code: empId,
    role: String(schedulingGroupFor(emp)).toLowerCase(),
    start,
    end,
    hours,
    drifts: false,
    source,
  });
}

/** Build Gantt-style draft slots from the live schedule grid (source of truth for manual edits). */
export function draftSlotsFromScheduleDay(storeEmployees, weekSchedule, dayKeyStr) {
  const slots = [];
  if (!dayKeyStr || !weekSchedule) return slots;
  (storeEmployees || []).forEach((emp) => {
    const empId = rosterScheduleKey(emp);
    const val = weekSchedule?.[empId]?.[dayKeyStr] || "";
    pushTimeDraftSlot(slots, emp, empId, val, "internal");
  });
  return slots;
}

/**
 * Draft slots as shown in the grid: Internal when set, otherwise SoftOne fill
 * (same rule as softoneValueWhenInternalBlank / people-cost SoftOne hours).
 */
export function draftSlotsFromDisplayedDay(
  storeEmployees,
  weekSchedule,
  dayKeyStr,
  foxlineSlotByKey,
  dateISO,
) {
  const slots = [];
  if (!dayKeyStr || !dateISO) return slots;
  (storeEmployees || []).forEach((emp) => {
    const empId = rosterScheduleKey(emp);
    const local = weekSchedule?.[empId]?.[dayKeyStr] || "";
    const foxSlot = foxlineSlotByKey?.[slotKey(empId, dateISO)];
    const softoneFill = softoneValueWhenInternalBlank(foxSlot, local);
    if (local) {
      pushTimeDraftSlot(slots, emp, empId, local, "internal");
      return;
    }
    if (softoneFill) {
      pushTimeDraftSlot(slots, emp, empId, softoneFill, "softone");
    }
  });
  return slots;
}

export function mergeSidebarDraftSlots(scheduleSlots, ganttSlots) {
  const merged = [];
  const seen = new Set();
  const add = (slot) => {
    if (!slot || isDraftSlotPlaceholder(slot)) return;
    const key = `${String(slot.employee_code || slot.employee_name || "").toLowerCase()}|${slot.start}|${slot.end}|${slot.role}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(slot);
  };
  (scheduleSlots || []).forEach(add);
  (ganttSlots || []).forEach(add);
  return merged;
}

function draftCoversOptimalWindow(draft, opt) {
  if (ganttDisplayRoleKey(draft?.role) !== ganttDisplayRoleKey(opt?.role)) return false;
  const dStart = shiftTimeToMins(draft?.start);
  let dEnd = shiftTimeToMins(draft?.end);
  const oStart = shiftTimeToMins(opt?.start);
  let oEnd = shiftTimeToMins(opt?.end);
  if (dStart == null || dEnd == null || oStart == null || oEnd == null) return false;
  if (dEnd <= dStart) dEnd += 24 * 60;
  if (oEnd <= oStart) oEnd += 24 * 60;
  const optDuration = oEnd - oStart;
  if (optDuration <= 0) return false;
  const overlap = Math.max(0, Math.min(dEnd, oEnd) - Math.max(dStart, oStart));
  return overlap / optDuration >= 0.5;
}

/** Recompute missing/extra/gap for a day after overlaying displayed (Internal+SoftOne) drafts. */
export function recomputeOptimalDayFromDrafts(day) {
  if (!day) return day;
  const optimalSlots = Array.isArray(day.optimal_slots) ? day.optimal_slots : [];
  const draftSlots = (Array.isArray(day.draft_slots) ? day.draft_slots : []).filter(
    (s) => !isDraftSlotPlaceholder(s),
  );
  const optUnits = [];
  optimalSlots.forEach((opt) => {
    const count = Math.max(1, Number(opt?.count) || 1);
    for (let i = 0; i < count; i += 1) optUnits.push(opt);
  });
  const usedDraft = new Set();
  const coveredOpt = new Set();
  optUnits.forEach((opt, oi) => {
    const di = draftSlots.findIndex((d, idx) => !usedDraft.has(idx) && draftCoversOptimalWindow(d, opt));
    if (di >= 0) {
      usedDraft.add(di);
      coveredOpt.add(oi);
    }
  });
  const missingMap = new Map();
  optUnits.forEach((opt, oi) => {
    if (coveredOpt.has(oi)) return;
    const role = String(opt?.role || "");
    const start = String(opt?.start || "").slice(0, 5);
    const end = String(opt?.end || "").slice(0, 5);
    const key = `${ganttDisplayRoleKey(role)}|${start}|${end}`;
    const hoursOne = Number(opt?.hours) || hoursBetweenHhmm(start, end) || 0;
    const prev = missingMap.get(key);
    if (prev) {
      prev.remaining_count += 1;
      prev.gap_hours = Math.round((prev.remaining_count * hoursOne) * 100) / 100;
      prev.count += 1;
    } else {
      missingMap.set(key, {
        role,
        start,
        end,
        remaining_count: 1,
        count: 1,
        gap_hours: Math.round(hoursOne * 100) / 100,
      });
    }
  });
  const missing_slots = Array.from(missingMap.values());
  const extra_slots = [];
  draftSlots.forEach((d, idx) => {
    if (usedDraft.has(idx)) return;
    const matchesAny = optUnits.some((opt) => draftCoversOptimalWindow(d, opt));
    if (!matchesAny) {
      extra_slots.push({
        role: d.role,
        start: d.start,
        end: d.end,
        hours: Number(d.hours) || hoursBetweenHhmm(d.start, d.end) || 0,
      });
    }
  });
  const draft_slots = draftSlots.map((d) => ({
    ...d,
    drifts: !optUnits.some((opt) => draftCoversOptimalWindow(d, opt)),
  }));
  const gap_hours = Math.round(
    missing_slots.reduce((sum, m) => sum + Number(m.gap_hours || 0), 0) * 100,
  ) / 100;
  const day_optimal_hours = Math.round(
    optimalSlots.reduce(
      (sum, o) => sum + (Number(o.hours) || 0) * Math.max(1, Number(o.count) || 1),
      0,
    ) * 100,
  ) / 100;
  const drift_pct = day_optimal_hours > 0
    ? Math.round((gap_hours / day_optimal_hours) * 10000) / 100
    : 0;
  return {
    ...day,
    draft_slots,
    missing_slots,
    extra_slots,
    gap_hours,
    drift_pct,
  };
}

/**
 * Overlay SoftOne-displayed shifts into optimal-schedule gantt days so vs Optimal
 * matches the Schedule grid (which shows SoftOne when Internal is blank).
 */
export function enrichGanttDataWithDisplayedSchedule(
  ganttData,
  { storeEmployees, weekSchedule, weekDates, dayKeyFn, toLocalISOFn, foxlineSlotByKey },
) {
  if (!ganttData || !Array.isArray(ganttData.days)) return ganttData;
  const days = ganttData.days.map((day) => {
    const dateISO = String(day?.date || "");
    const idx = (weekDates || []).findIndex((d) => toLocalISOFn(d) === dateISO);
    if (idx < 0) return day;
    const dayKeyStr = dayKeyFn(weekDates[idx], idx);
    const displayed = draftSlotsFromDisplayedDay(
      storeEmployees,
      weekSchedule,
      dayKeyStr,
      foxlineSlotByKey,
      dateISO,
    );
    const ganttDraft = (day.draft_slots || []).filter((s) => !isDraftSlotPlaceholder(s));
    const mergedDraft = mergeSidebarDraftSlots(displayed, ganttDraft);
    if (mergedDraft.length === 0 && ganttDraft.length === (day.draft_slots || []).length) {
      return day;
    }
    return recomputeOptimalDayFromDrafts({ ...day, draft_slots: mergedDraft });
  });
  const total_gap_hours = Math.round(
    days.reduce((sum, d) => sum + Number(d?.gap_hours || 0), 0) * 100,
  ) / 100;
  const total_optimal_hours = Math.round(
    days.reduce(
      (sum, d) =>
        sum +
        (d?.optimal_slots || []).reduce(
          (s, o) => s + (Number(o?.hours) || 0) * Math.max(1, Number(o?.count) || 1),
          0,
        ),
      0,
    ) * 100,
  ) / 100;
  const days_with_gaps = days.filter((d) => Number(d?.gap_hours) > 0).length;
  const drift_pct = total_optimal_hours > 0
    ? Math.round((total_gap_hours / total_optimal_hours) * 10000) / 100
    : 0;
  return {
    ...ganttData,
    days,
    summary: {
      ...(ganttData.summary || {}),
      total_gap_hours,
      days_with_gaps,
      drift_pct,
    },
  };
}

// Shift presets per hour type
export const SHIFTS_8H = [
  "09:00-17:00",
  "10:00-18:00",
  "11:00-19:00",
  "12:00-20:00",
  "13:00-21:00",
  "14:00-22:00",
  "15:00-23:00",
  "16:00-00:00",
  "17:00-01:00",
  "18:00-02:00",
  "19:00-03:00",
];
export const SHIFTS_6H = [
  "09:00-15:00",
  "10:00-16:00",
  "11:00-17:00",
  "12:00-18:00",
  "12:30-18:30",
  "13:00-19:00",
  "14:00-20:00",
  "15:00-21:00",
  "16:30-22:30",
  "17:00-23:00",
  "20:00-02:00",
  "21:00-03:00",
];

export function normalizeContractedShiftHours(empOrHours) {
  const raw =
    typeof empOrHours === "object" && empOrHours != null
      ? empOrHours.shiftHours ?? empOrHours.shift_hours
      : empOrHours;
  if (raw == null || raw === "") return 8;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 8;
  return Math.round(n * 2) / 2;
}

export function formatShiftPresetTime(totalMinutes) {
  const mins = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const hour = Math.floor(mins / 60);
  const minute = mins % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/** Hourly start slots (09:00+) for arbitrary contracted duration; 6H/8H use fixed arrays. */
export function generateHourlyShiftPresets(durationHours) {
  const duration = normalizeContractedShiftHours(durationHours);
  if (duration === 8) return SHIFTS_8H;
  if (duration === 6) return SHIFTS_6H;
  const durationMins = Math.round(duration * 60);
  const presets = [];
  for (let startH = 9; startH < 24; startH++) {
    const endMins = startH * 60 + durationMins;
    if (endMins > 24 * 60) {
      const wrappedMins = endMins % (24 * 60);
      const wrappedHour = Math.floor(wrappedMins / 60);
      // Only offer overnight presets that end in the early-morning window (00–05).
      if (!NEXT_DAY_END_HOURS.includes(wrappedHour)) break;
    }
    presets.push(
      `${formatShiftPresetTime(startH * 60)}-${formatShiftPresetTime(endMins)}`
    );
  }
  return presets;
}

export function shiftPresetsForHours(hours, customShifts6 = [], customShifts8 = []) {
  const duration = normalizeContractedShiftHours(hours);
  if (duration === 8) return [...SHIFTS_8H, ...customShifts8];
  if (duration === 6) return [...SHIFTS_6H, ...customShifts6];
  return generateHourlyShiftPresets(duration);
}

/** Contracted daily hours in use among active Foxline PG store employees. */
export const CONTRACTED_SHIFT_HOURS = Object.freeze([4, 5, 6, 7, 8, 10]);

export function contractedShiftHoursSelectOptions() {
  return CONTRACTED_SHIFT_HOURS.map((hours) => ({ v: hours, l: String(hours) }));
}
export const SPECIAL_STATUSES = [
  { value: "OFF",        label: "Rest Day",   labelGr: "Ρεπό",       color: "#FBBF24", textColor: "#92400E" },
  { value: "NO_WORK",    label: "No Work",    labelGr: "Μη εργασία", color: "#94A3B8", textColor: "#1E293B" },
  { value: "ΑΔΕΙΑ",      label: "Leave",      labelGr: "Άδεια",      color: "#22C55E", textColor: "#14532D" },
  { value: "ΑΝΑΡΡΩΤΙΚΗ", label: "Sick Leave", labelGr: "Αναρρωτική", color: "#F97316", textColor: "#7C2D12" },
  { value: "OTHER_STORE", label: "Other Store",labelGr: "Άλλο κατ.",  color: "#3B82F6", textColor: "#FFFFFF" },
];

export function specialStatusDisplay(val, lang) {
  const sp = SPECIAL_STATUSES.find((s) => s.value === val);
  if (!sp) return String(val ?? "");
  return lang === "gr" ? sp.labelGr : sp.label;
}

/** True when leave request type is a rest-day (ρεπό) submission — not sick/annual/etc. */
export function isRestDayLeaveRequestType(type) {
  const t = String(type || "").trim().toLowerCase();
  return t === "rest_day" || t === "repo";
}

/**
 * Match schedule Rest Day cell to a formal leave/rest-day request (any status).
 * Prefer employee_code when present; fall back to name / email (create path stores name+email only).
 */
export function findRequestedRestDayRow(weekLeaveRequests, emp, dateISO) {
  const targetDate = String(dateISO || "").slice(0, 10);
  if (!targetDate) return null;
  const empCode = String(rosterScheduleKey(emp) || "").trim();
  const empName = String(emp?.name || "").trim().toUpperCase();
  const empEmail = String(emp?.email || "").trim().toLowerCase();

  return (weekLeaveRequests || []).find((req) => {
    if (!isRestDayLeaveRequestType(req?.leave_type ?? req?.type)) return false;
    const reqDate = String(req?.leave_date || req?.date || "").slice(0, 10);
    if (reqDate !== targetDate) return false;

    const reqCode = String(req?.employee_code || "").trim();
    if (reqCode && empCode && reqCode === empCode) return true;

    const reqName = String(req?.employee_name || "").trim().toUpperCase();
    if (reqName && empName && reqName === empName) return true;

    const reqEmail = String(req?.employee_email || "").trim().toLowerCase();
    if (reqEmail && empEmail && reqEmail === empEmail) return true;

    return false;
  }) || null;
}

export function hasRequestedRestDay(weekLeaveRequests, emp, dateISO) {
  return Boolean(findRequestedRestDayRow(weekLeaveRequests, emp, dateISO));
}

/** Stable key for optimistic manager-marked rest days (survives ShiftPicker remount). */
export function restDayPendingKey(emp, dateISO) {
  const date = String(dateISO || "").slice(0, 10);
  if (!date) return "";
  const code = String(rosterScheduleKey(emp) || "").trim();
  if (code) return `${code}|${date}`;
  const name = String(emp?.name || "").trim().toUpperCase();
  return name ? `name:${name}|${date}` : "";
}

export function isRestDayMarkedRequested(weekLeaveRequests, pendingLocalRestRequests, emp, dateISO) {
  if (hasRequestedRestDay(weekLeaveRequests, emp, dateISO)) return true;
  const key = restDayPendingKey(emp, dateISO);
  return Boolean(key && pendingLocalRestRequests && pendingLocalRestRequests.has(key));
}

/** Same match as the row badge (`a.emp === emp.name`). First-name overlap must not pull in another person. */
export function filterScheduleWarningsForEmployee(warnings, emp) {
  return (warnings || []).filter((w) => {
    const wName = (w.employeeName || w.employee_name || w.name || w.emp || "").toLowerCase().trim();
    const wId = w.employeeId ?? w.employee_id ?? w.id;
    const empName = (emp.name || "").toLowerCase().trim();
    const empId = emp.id;
    if (wId != null && empId != null && String(wId) === String(empId)) return true;
    if (wName && empName && wName === empName) return true;
    return false;
  });
}

export const COVERAGE_HEATMAP_TOOLTIP_BG = "#0D2B1D";

export function warningRowLabel(t, lang) {
  if (t === "rest" || t === "rest_violation") return lang === "gr" ? "Παράβαση ανάπαυσης" : "Rest violation";
  if (t === "shifts" || t === "shift_count") return lang === "gr" ? "Αναντιστοιχία βαρδιών" : "Shift count mismatch";
  if (t === "empty" || t === "empty_day") return lang === "gr" ? "Κενή μέρα" : "Empty day";
  if (t === "coverage" || t === "coverage_gap" || t === "role") return lang === "gr" ? "Κενό κάλυψης" : "Coverage gap";
  return lang === "gr" ? "Προειδοποίηση" : "Warning";
}

export function warningRowDotColor(t) {
  if (t === "rest" || t === "rest_violation" || t === "coverage" || t === "coverage_gap" || t === "role") return "#EF4444";
  if (t === "shifts" || t === "shift_count" || t === "empty" || t === "empty_day") return "#F59E0B";
  return "#10B981";
}

export function warningRowDetailRight(w, lang, openingHours) {
  const t = w.type;
  const msg = lang === "gr" ? (w.msgGr || w.msg) : w.msg;
  const msgEn = w.msg || "";
  if (t === "rest" || t === "rest_violation") {
    const m = String(msgEn).match(/Only ([\d.]+)h rest/i);
    const gap = m ? m[1] : null;
    return gap ? `${gap}h rest gap` : "—";
  }
  if (t === "shifts" || t === "shift_count") {
    let m = String(msgEn).match(/(\d+)-day contract but only has (\d+) work shifts/i);
    if (m) return `${m[2]}/${m[1]} days`;
    m = String(msg).match(/(\d+) μέρες\/εβδ\.\s*αλλά μόνο (\d+) βάρδιες/i);
    if (m) return `${m[2]}/${m[1]} days`;
    return "—";
  }
  if (t === "empty" || t === "empty_day") {
    let m = String(msgEn).match(/(\d+) day\(s\) not filled/i);
    if (m) return `${m[1]} days`;
    m = String(msg).match(/(\d+)\s*μέρα/i);
    if (m) return `${m[1]} days`;
    return lang === "gr" ? "Πολλαπλές" : "Multiple";
  }
  if (t === "coverage" || t === "coverage_gap" || t === "role") {
    const day = w.day || "";
    const oh = day && openingHours ? openingHours[day] : "";
    if (day && oh) return `${day} ${oh}`;
    return day || "—";
  }
  return "";
}

export function EmployeeWarningRichTooltip({ warnings, emp, lang, openingHours = {} }) {
  const empWarnings = filterScheduleWarningsForEmployee(warnings, emp);
  const rows = empWarnings.slice(0, 5);
  const n = empWarnings.length;
  const headerLine =
    n === 0
      ? emp.name
      : lang === "gr"
        ? `${emp.name} · ${n} ${n === 1 ? "ζήτημα" : "ζητήματα"}`
        : `${emp.name} · ${n} issue${n === 1 ? "" : "s"}`;
  const onlyShiftMismatch =
    empWarnings.length > 0 && empWarnings.every((w) => w.type === "shifts" || w.type === "shift_count");
  const footerHint = onlyShiftMismatch
    ? lang === "gr"
      ? "Προσαρμόστε τις συμβατικές μέρες ή το πρόγραμμα"
      : "Adjust contracted days or schedule"
    : lang === "gr"
      ? "Ανοίξτε το πρόγραμμα για διόρθωση"
      : "Open schedule to resolve";

  return (
    <>
      <div
        style={{
          fontWeight: 800,
          fontSize: 11,
          color: "#F59E0B",
          marginBottom: 8,
        }}
      >
        {headerLine}
      </div>
      {!rows.length ? (
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.5 }}>
          {lang === "gr" ? "Λεπτομέρειες μη διαθέσιμες." : "No details available"}
        </div>
      ) : (
        <div style={{ maxHeight: 200, overflowY: "auto" }}>
          {rows.map((w, idx) => (
            <div
              key={`wt-${idx}-${w.type}`}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: warningRowDotColor(w.type),
                  flexShrink: 0,
                }}
              />
              <span style={{ fontWeight: 700, fontSize: 12, color: "#fff", flex: 1, minWidth: 0 }}>
                {warningRowLabel(w.type, lang)}
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.6)",
                  textAlign: "right",
                  flexShrink: 0,
                  maxWidth: 120,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {warningRowDetailRight(w, lang, openingHours)}
              </span>
            </div>
          ))}
        </div>
      )}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.15)",
          marginTop: 8,
          paddingTop: 8,
          fontSize: 10,
          fontStyle: "italic",
          color: "rgba(255,255,255,0.5)",
        }}
      >
        {footerHint}
      </div>
    </>
  );
}
/** First word of employee_name — shared by Gantt Day/Week. */
export function employeeDisplayNameFirstWord(employeeName, staffList = []) {
  return String(resolveEmployeeName(employeeName, staffList) || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)[0] || "";
}

export function dailyTargetStaffingState(scheduled, target, dayClosed, noTargetData) {
  if (dayClosed || noTargetData) return { kind: "neutral" };
  const t = Number(target);
  const s = Number(scheduled);
  if (!Number.isFinite(t) || t <= 0) return { kind: "neutral" };
  const sched = Number.isFinite(s) ? s : 0;
  if (sched > t) return { kind: "over", border: "#EF4444", pill: "Over target", pillBg: "#FEE2E2", pillColor: "#B91C1C" };
  if (sched === t) return { kind: "at", border: "#F59E0B", pill: "At target", pillBg: "#FEF3C7", pillColor: "#92400E" };
  if (sched >= t * 0.9) return { kind: "near", border: "#F59E0B", pill: "Near target", pillBg: "#FEF3C7", pillColor: "#92400E" };
  return { kind: "below", border: "#EF4444", pill: "Below target", pillBg: "#FEE2E2", pillColor: "#B91C1C" };
}


